import useUpdateRecord from "api/api-hooks/records/useUpdateRecord";
import SectionRenderer from "components/Records/SectionRenderer";
import pluralize from "pluralize";
import { useCallback, useMemo, useState } from "react";
import { useEffect } from "react";
import { Button } from "reactstrap";
import { callFunctionIfExists } from "utils/GeneralUtils";
import InstanceUtils from "utils/InstanceUtils";
import StrUtils from "utils/StrUtils";
import AppDrawer from "../AppDrawer";
import { generatePrefill } from "utils/QuickCreateUtils";
import useUser from "api/api-hooks/auth/useUser";
import AppSpinner from "../../../elements/AppSpinner";
import { safeObjectMerge } from "../../../utils/GeneralUtils";

const noFields = [];

/**
 * This Drawer is currently used for 4 separate things
 * 1 - Creating a Subitem (supports prefill)
 * 2 - Updating a Subitem
 * 3 - Adding a Subitem to a MultiSubitem (supports prefill)
 * 4 - Updating a Subitem in a MultiSubitem
 */
const UpdateSubitemDrawer = ({
  open,
  onClose,
  records,
  fieldSchema,
  parentRecord,
  addSubitemMode = false, // Set to true to use Drawer as AddSubitemDrawer for MultiSubitems
  onCreate,
  prefill = null,
}) => {
  const { user, userLoading } = useUser();

  const fields = useMemo(
    () =>
      fieldSchema?.SubitemFields?.filter((f) => {
        if (f?.APIName === "Tags") return true;
        if (f?.SystemField) return false;
        return true;
      }) ?? noFields,
    [fieldSchema?.SubitemFields]
  );
  const [payload, setPayload] = useState(null);
  const { updateRecord, recordUpdating, updateRecordError } = useUpdateRecord();
  const [payloadWithPrefill, setPayloadWithPrefill] = useState({});

  const defaultSubitem = useMemo(() => {
    const payload = {
      _ModuleAPIName: parentRecord?._ModuleAPIName,
    };

    for (const field of fields) {
      payload[field.APIName] = field?.DefaultValue ?? "";
    }

    if (prefill?.length) {
      for (const pre of prefill) {
        payload[pre.Field] = pre.Value;
      }
    }

    return payload;
  }, [fields, parentRecord?._ModuleAPIName, prefill]);

  const getStartingPayload = useCallback(() => {
    // Handle custom add subitem mode
    if (addSubitemMode) {
      return defaultSubitem;
    }

    if (!records) {
      return fieldSchema?.FieldType === "Subitem"
        ? defaultSubitem
        : [defaultSubitem];
    }

    return Array.isArray(records) ? [...records] : { ...(records || {}) };
  }, [defaultSubitem, fieldSchema?.FieldType, records, addSubitemMode]);

  const handleClose = () => {
    onClose();
    setPayload(getStartingPayload());
  };

  const handleChange = (field, val) => {
    if (!Array.isArray(payload)) {
      return setPayload((prev) => ({ ...prev, [field.APIName]: val }));
    }

    setPayload(val);
  };

  const save = (finalPayload) => {
    updateRecord({
      field: fieldSchema,
      payload: finalPayload,
      moduleAPIName: parentRecord._ModuleAPIName,
      recordID: parentRecord.id,
      subitemID: fieldSchema.FieldType === "Subitem" ? null : null,
      onSuccess: (newRecord) => {
        onClose();
        callFunctionIfExists(onCreate, newRecord);
      },
    });
  };

  const handleAddSubitemModeSave = (pload) => {
    let newSubitem = { ...pload, id: StrUtils.uuidv4() };

    const finalPayload = {
      [fieldSchema.APIName]: newSubitem,
    };

    save(finalPayload);
  };

  const handleSave = async () => {
    let pload;

    if (fieldSchema.FieldType === "MultiSubitem" && !addSubitemMode) {
      const newPayload = [];
      for (const subitem of payload) {
        const subitemPayload = await InstanceUtils.formatRecordCreationPayload(
          fieldSchema?.SubitemFields,
          subitem
        );
        newPayload.push(subitemPayload);
      }

      pload = newPayload;
    } else {
      pload = await InstanceUtils.formatRecordCreationPayload(
        fieldSchema?.SubitemFields,
        payload
      );
    }

    if (addSubitemMode) {
      return handleAddSubitemModeSave(pload);
    }

    let finalPayload = { [fieldSchema.APIName]: {} };

    if (fieldSchema.FieldType === "MultiSubitem") {
      finalPayload[fieldSchema.APIName] = pload;
    }

    if (fieldSchema.FieldType === "Subitem") {
      if (records) {
        for (const key of Object.keys(pload)) {
          if (pload[key] !== records[key]) {
            finalPayload[fieldSchema.APIName][key] = pload[key];
          }
        }
      } else {
        // Handle when no Subitem Exists.
        finalPayload = {
          [fieldSchema.APIName]: { ...defaultSubitem, ...pload },
        };
      }
    }

    save(finalPayload);
  };

  // Setup Drawer. Specifically Payload/Prefill
  useEffect(() => {
    if (userLoading) return;

    // Get the starting payload
    const startingPayload = getStartingPayload();

    if (Array.isArray(startingPayload)) return setPayload(startingPayload);

    let variableVals = {
      user: {
        id: user?.id,
        _RecordName: user?.Name,
        FirstName: StrUtils.getFirstName(user?.Name),
        LastName: StrUtils.getLastName(user?.Name),
        ModuleAPIName: "Contacts",
      },
    };

    const result = generatePrefill(
      prefill,
      variableVals,
      fieldSchema?.SubitemFields
    );

    const reducedResult = result.reduce((obj, prefill) => {
      obj[prefill.Field] = prefill.Value;
      return obj;
    }, {});

    const mergedPayload = safeObjectMerge(startingPayload, reducedResult);
    setPayload(mergedPayload);
    setPayloadWithPrefill(structuredClone(mergedPayload));
  }, [
    getStartingPayload,
    prefill,
    fieldSchema,
    user?.id,
    user?.Name,
    userLoading,
  ]);

  return (
    <AppDrawer
      open={open}
      onClose={handleClose}
      backdrop="static"
      headerTitle={
        addSubitemMode && !prefill
          ? `Add ${pluralize.singular(fieldSchema?.Name ?? "Subitem")}`
          : "Update Subitem"
      }
      headerDescription={
        addSubitemMode && !prefill
          ? `Adding a subitem to ${fieldSchema?.Name}`
          : "Updating subitem"
      }
      headerOptions={
        <Button
          size="sm"
          onClick={handleSave}
          color="primary"
          style={{ width: 80 }}
          disabled={recordUpdating}
        >
          {recordUpdating ? <AppSpinner size="sm" color="white" /> : "Save"}
        </Button>
      }
      bodyCSS="pt-2"
      body={
        <SectionRenderer
          section={{
            Type: addSubitemMode ? "Subitem" : fieldSchema?.FieldType,
            Fields: fields.map((f) => ({
              Field: f,
              ColSpan: 1,
            })),
            DefaultSubitem: defaultSubitem,
            ParentField: fieldSchema,
            BackgroundColor: "bg-white",
          }}
          module={{ Fields: fields }}
          createMode={true}
          values={payloadWithPrefill}
          handleChange={handleChange}
        />
      }
    />
  );
};

export default UpdateSubitemDrawer;
